import React, { useState, useContext, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, UserPlus, Building, Users, Eye, EyeOff } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

function Register() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    user_type: 'PARTICULIER',
    nb_personnes: '',
    magasin_id: '',
    secteur_activite: '',
    invitation_token: '',
    verification_token: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useContext(AuthContext);

  useEffect(() => {
    const invitationToken = searchParams.get('invitation_token');
    if (invitationToken) {
      setFormData(prev => ({ ...prev, invitation_token: invitationToken }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const submitData = {
      ...formData,
      nb_personnes: formData.user_type === 'PARTICULIER' && formData.nb_personnes ? parseInt(formData.nb_personnes) : null,
      magasin_id: formData.user_type === 'PRO' && formData.magasin_id ? formData.magasin_id : null,
      secteur_activite: formData.user_type === 'PRO' && formData.secteur_activite ? formData.secteur_activite : null,
      invitation_token: formData.invitation_token || undefined,
      verification_token: formData.verification_token || undefined
    };

    if (!emailVerified) {
      toast.error('Veuillez verifier votre email avec le code reçu');
      setLoading(false);
      return;
    }
    
    const success = await register(submitData);
    
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'email') {
      setEmailVerified(false);
      setVerificationCode('');
      setFormData((prev) => ({ ...prev, verification_token: '' }));
    }
  };

  const sendVerificationCode = async () => {
    if (!formData.email) {
      toast.error('Veuillez saisir un email valide');
      return;
    }

    setSendingCode(true);
    try {
      await axios.post('/auth/request-verification-code', { email: formData.email });
      toast.success('Code de verification envoye par email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi du code');
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode) {
      toast.error('Saisissez le code de verification');
      return;
    }

    setVerifyingCode(true);
    try {
      const res = await axios.post('/auth/verify-email-code', {
        email: formData.email,
        code: verificationCode
      });
      setFormData((prev) => ({ ...prev, verification_token: res.data.verification_token }));
      setEmailVerified(true);
      toast.success('Email verifie avec succes');
    } catch (error) {
      setEmailVerified(false);
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleUserTypeChange = (value) => {
    setFormData({
      ...formData,
      user_type: value,
      // Reset champs spécifiques
      nb_personnes: '',
      magasin_id: '',
      secteur_activite: ''
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="compact" />
        </div>
        {/* Logo et titre */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-3">
            <div className="bg-gradient-primary p-3 sm:p-4 rounded-2xl shadow-lg">
              <Package className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">InventoTrack</h1>
          <div className="inline-block bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-3">
            v1.0.0
          </div>
          <p className="text-sm sm:text-base text-gray-600">{t('auth.register')}</p>
          
          {/* Pitch descriptif */}
          <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-lg p-4 mt-4 text-left">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-emerald-700">🏠 Gérez votre inventaire familial</strong>
              <br className="mt-2" />
              InventoTrack vous aide à suivre votre stock alimentaire et d'articles du quotidien. Sachez toujours ce que vous avez, ce qui manque, et estimez vos dépenses avec nos outils intelligents.
            </p>
          </div>
        </div>

        <Card className="card animate-fadeInUp shadow-2xl border-0">
          <CardHeader className="text-center pb-2 px-4 sm:px-6">
            <CardTitle className="text-xl sm:text-2xl font-semibold text-gray-900">{t('auth.register')}</CardTitle>
            <CardDescription className="text-gray-500">
              Rejoignez InventoTrack dès aujourd'hui
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                    Nom d'utilisateur
                  </Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Choisissez un nom d'utilisateur"
                    data-testid="register-username-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <div className="space-y-2">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="votre.email@exemple.com"
                      data-testid="register-email-input"
                    />
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Code email"
                        className="form-input"
                        data-testid="register-email-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={sendVerificationCode}
                        disabled={sendingCode || !formData.email}
                        data-testid="send-email-code-button"
                      >
                        {sendingCode ? 'Envoi...' : 'Envoyer code'}
                      </Button>
                      <Button
                        type="button"
                        onClick={verifyCode}
                        disabled={verifyingCode || !verificationCode}
                        data-testid="verify-email-code-button"
                      >
                        {verifyingCode ? 'Vérif...' : 'Vérifier'}
                      </Button>
                    </div>
                    {emailVerified && (
                      <p className="text-xs text-emerald-600 font-medium">Email vérifié</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="form-input pr-12"
                      placeholder="Créez un mot de passe sécurisé"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Type de compte
                  </Label>
                  <div className="flex items-center space-x-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <Users size={18} className="text-emerald-700" />
                    <span className="text-gray-700 font-medium">Particulier (Foyer)</span>
                  </div>
                  <input type="hidden" name="user_type" value="PARTICULIER" />
                </div>

                {/* Champs spécifiques aux particuliers */}
                {formData.user_type === 'PARTICULIER' && (
                  <div className="space-y-2 animate-fadeInUp">
                    <Label htmlFor="nb_personnes" className="text-sm font-medium text-gray-700">
                      Nombre de personnes dans le foyer
                    </Label>
                    <Input
                      id="nb_personnes"
                      name="nb_personnes"
                      type="number"
                      min="1"
                      max="20"
                      value={formData.nb_personnes}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Ex: 4"
                      data-testid="register-nb-personnes-input"
                    />
                  </div>
                )}

                {/* Champs spécifiques aux professionnels */}
                {formData.user_type === 'PRO' && (
                  <div className="space-y-4 animate-fadeInUp">
                    <div className="space-y-2">
                      <Label htmlFor="magasin_id" className="text-sm font-medium text-gray-700">
                        Identifiant magasin/établissement
                      </Label>
                      <Input
                        id="magasin_id"
                        name="magasin_id"
                        type="text"
                        value={formData.magasin_id}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Ex: MAGASIN001"
                        data-testid="register-magasin-id-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="secteur_activite" className="text-sm font-medium text-gray-700">
                        Secteur d'activité
                      </Label>
                      <Select 
                        value={formData.secteur_activite} 
                        onValueChange={(value) => setFormData({...formData, secteur_activite: value})}
                      >
                        <SelectTrigger className="form-input h-12" data-testid="register-secteur-select">
                          <SelectValue placeholder="Sélectionnez votre secteur" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="epicerie">Épicerie</SelectItem>
                          <SelectItem value="boulangerie">Boulangerie</SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="supermarche">Supermarché</SelectItem>
                          <SelectItem value="pharmacie">Pharmacie</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !emailVerified}
                className="w-full btn-modern btn-primary h-12 text-base font-semibold"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="loading-spinner w-5 h-5 border-white"></div>
                    <span>Création du compte...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <UserPlus size={20} />
                    <span>Créer mon compte</span>
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-4 pt-6">
            <div className="text-center text-sm text-gray-600">
              Déjà un compte ?
            </div>
            <Link to="/login" className="w-full">
              <Button 
                variant="outline" 
                className="w-full btn-modern btn-outline h-12 text-base font-semibold"
                data-testid="register-login-link"
              >
                Se connecter
              </Button>
            </Link>
            <Link to="/privacy-policy" className="text-xs text-emerald-700 underline" data-testid="privacy-policy-link-register">
              Politique de confidentialité
            </Link>
          </CardFooter>
        </Card>

        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2026 InventoTrack. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
}

export default Register;